/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import orderBy from 'lodash.orderby';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useHistory } from 'react-router';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LimitedAccessToFeaturesMessageBar } from 'src/core/components/LimitedAccessToFeaturesMessageBar';
import { Page } from 'src/core/components/Page';
import { RenameModal, RenameModalDto } from 'src/core/components/RenameModal';
import { TablePagination } from 'src/core/components/TablePagination';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { usePagination } from 'src/core/hooks/usePagination';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { getEventLogger } from 'src/core/services/telemetry/logEventBuilder';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { routeTable } from 'src/core/utils/routeTable';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { DomainModelsModal } from 'src/luis/appShell/buildPane/domainsPage/DomainModelsModal';
import { IntentCreateModal, IntentCreateModalDto } from 'src/luis/appShell/buildPane/intentsPage/components/IntentCreateModal';
import { IntentDeleteModal, IntentDeleteModalDto } from 'src/luis/appShell/buildPane/intentsPage/components/IntentDeleteModal';
import { IntentTable } from 'src/luis/appShell/buildPane/intentsPage/components/IntentTable';
import { IntentTableCommandBar } from 'src/luis/appShell/buildPane/intentsPage/components/IntentTableCommandBar';
import { intentsPageMessages } from 'src/luis/appShell/buildPane/intentsPage/intentsPageMessages';
import { useCurrentVersionedAppId } from 'src/luis/modules/apps/hooks/useCurrentVersionedAppId';
import { PhraseListModalContainer } from 'src/luis/modules/features/components/PhraseListModalContainer';
import { useConversationFeatures } from 'src/luis/modules/features/hooks/useConversationFeatures';
import { Intent } from 'src/luis/modules/intents/Intent';
import { useIntents } from 'src/luis/modules/intents/useIntents';
import { LuisId } from 'src/luis/types/Resource';

const defaultModalStates = {
    isCreateIntentModalHidden: true,
    isRenameIntentModalHidden: true,
    isDeleteIntentModalHidden: true,
    isPhraseListCreationModalHidden: true,
    isPrebuiltDomainIntentModalHidden: true
};

/**
 * Represents a page container component for intents.
 */
export const IntentsPage = observer(() => {
    const { intents, isLoading: areIntentsLoading } = useIntents();
    const { dispatcher, rootStore } = useGlobalState();
    const { selection, selectedItems } = useSelection<Intent>();
    const [query, setQuery] = React.useState('');
    const [modalsState, setModalsState] = useUpdateState(defaultModalStates);
    const { localize } = useLocalization();
    const history = useHistory();
    const { features } = useConversationFeatures();
    const { appId, versionId } = useCurrentVersionedAppId();
    const eventLoggerRef = React.useRef(getEventLogger('IntentsPage'));

    const { userRole, displayedUserRole } = rootStore.serviceKey;

    const { canWrite, canDeleteData } = mapUserRoleToUserActions(userRole);

    // Ensures that the None intent row can not be selected.
    React.useEffect(() => {
        selection.canSelectItem = intent => (intent as Intent).name !== LuisConstants.Luis.NoneIntentName;
    }, []);

    const [sortKey, setSortKey] = React.useState<SortKeyType<Intent>>({ key: 'name', order: 'asc' });
    const filteredIntents = orderBy(
        substringSearchStrategy(intents, query, 'name'),
        [createKeyedStringNormalizer(sortKey.key)],
        [sortKey.order]
        // You have to provide a key for each item to let react handle the row updates caching stuff, it fixes this bug 14850027.
    ).map(i => ({ ...i, key: i.name }));

    const { currentPage, setCurrentPage, pageItems, pageCount } = usePagination(filteredIntents);

    const tracker = useProgressTracker<LuisId>({
        onComplete: (intentId: LuisId) => {
            setModalsState(defaultModalStates);
            selection.setAllSelected(false);

            if (intentId) {
                history.push(routeTable.luis.intentPage(appId, versionId, intentId));
            }
        }
    });

    const handleCreateIntent = ({ name }: IntentCreateModalDto) => {
        dispatcher.dispatch('createIntent', { name, tracker });
    };

    const handleDeleteIntent = ({ keepUtterancesAndPatterns }: IntentDeleteModalDto) => {
        dispatcher.dispatch('deleteIntents', {
            tracker,
            ids: selectedItems.map(intent => intent.id),
            deleteReferences: !keepUtterancesAndPatterns
        });
    };

    const handleRenameIntent = (data: RenameModalDto) => {
        dispatcher.dispatch('renameIntent', {
            tracker,
            name: data.name,
            id: selectedItems[0].id,
            toastData: {
                running: { title: localize(intentsPageMessages.renameIntentToastStart) },
                completed: { title: localize(intentsPageMessages.renameIntentToastSuccess) },
                failed: { title: localize(intentsPageMessages.renameIntentToastFailed) }
            }
        });
    };

    const onQueryChange = (searchQuery: string) => {
        eventLoggerRef.current.log('IntentTableSearchTriggered');
        setQuery(searchQuery);
    };

    return (
        <Page size="container-fluid" pageName="IntentsPage" title={localize(intentsPageMessages.pageTitle)}>
            <EditableTitle
                title={localize(intentsPageMessages.pageTitle)}
                description={localize(intentsPageMessages.pageTitleDescription)}
            />

            {!canWrite && <LimitedAccessToFeaturesMessageBar displayedUserRole={displayedUserRole} />}

            <IntentCreateModal
                disabled={tracker.isRunning}
                onSubmit={handleCreateIntent}
                hidden={modalsState.isCreateIntentModalHidden}
                onDismiss={() => setModalsState({ isCreateIntentModalHidden: true })}
            />

            <IntentDeleteModal
                disabled={tracker.isRunning}
                onDelete={handleDeleteIntent}
                hidden={modalsState.isDeleteIntentModalHidden}
                onDismiss={() => setModalsState({ isDeleteIntentModalHidden: true })}
            />

            <RenameModal
                disabled={tracker.isRunning}
                onSubmit={handleRenameIntent}
                hidden={modalsState.isRenameIntentModalHidden}
                name={selectedItems[0] ? selectedItems[0].name : ''}
                onDismiss={() => setModalsState({ isRenameIntentModalHidden: true })}
                dialogContentProps={{ title: localize(intentsPageMessages.renameIntent) }}
            />

            <IntentTableCommandBar
                query={query}
                onQueryChange={onQueryChange}
                selectedItems={selectedItems}
                userRole={userRole}
                displayedUserRole={displayedUserRole}
                onRenameIntent={() => setModalsState({ isRenameIntentModalHidden: false })}
                onCreateIntent={() => setModalsState({ isCreateIntentModalHidden: false })}
                onDeleteIntent={() => setModalsState({ isDeleteIntentModalHidden: false })}
                onAddPrebuiltIntent={() => setModalsState({ isPrebuiltDomainIntentModalHidden: false })}
            />

            <DomainModelsModal
                modelsType="intents"
                hidden={modalsState.isPrebuiltDomainIntentModalHidden}
                onDismiss={() => setModalsState({ isPrebuiltDomainIntentModalHidden: true })}
                onSubmit={() => setModalsState({ isPrebuiltDomainIntentModalHidden: true })}
            />

            <PhraseListModalContainer
                initData={{ isGlobal: false }}
                hidden={modalsState.isPhraseListCreationModalHidden}
                onSubmit={() => setModalsState({ isPhraseListCreationModalHidden: true })}
                onDismiss={() => setModalsState({ isPhraseListCreationModalHidden: true })}
            />

            <IntentTable
                sortKey={sortKey}
                items={pageItems}
                selection={selection}
                features={toJS(features)}
                isLoading={areIntentsLoading}
                areItemsFiltered={Boolean(query)}
                overallItemsCount={filteredIntents.length}
                onDeleteKeyUp={() => canDeleteData && setModalsState({ isDeleteIntentModalHidden: false })}
                onIntentClick={intentId => history.push(routeTable.luis.intentPage(appId, versionId, intentId))}
                onSortColumn={c => {
                    setSortKey({ key: c.key as keyof Intent, order: c.isSortedDescending ? 'asc' : 'desc' });
                    selection.setAllSelected(false);
                }}
            />

            <TablePagination pageCount={pageCount} currentPage={currentPage} onPageChange={p => setCurrentPage(p.selected)} />
        </Page>
    );
});
